import React from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';
import Radio from 'component/Layout/Form/Radio';

import classNames from 'classnames';

import { useCancellation } from 'hooks/form/filter';
import { useSpecialMeta } from 'hooks/form/meta';
import { useChangedFilterListForSection } from 'hooks/form/useChangedFilterList';
import { useBoUser } from 'hooks/useBoUser';
import { QaIdentifier } from 'hooks/useQa';

import { CancellationType } from 'types/Search/filter';
import { SearchParam } from 'types/Search/params';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    useDisabledStatus,
    MetaValue,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';
import { FilterKeys, SectionName } from '../types';

import { getSelected } from './__helper/getSelected';

const Cancellation = ({ disableWhen = MetaValue.NoResult }) => {
    const { t } = useTranslation();
    const [cancellation, setCancellation] = useCancellation();
    const [specialMeta] = useSpecialMeta();
    const isBoUser = useBoUser();
    const getDisabledStatus = useDisabledStatus(disableWhen);

    const { onSelect, onClear, isFilterChanged } =
        useChangedFilterListForSection(SectionName.Cancellation, () =>
            getSelected(cancellation).find((type) => cancellation === type)
        );

    return (
        <Segment
            withDeleteLink
            onDelete={() => {
                onClear();
                setCancellation(undefined);
            }}
            headline={{
                text: t('TRANS-cancellation'),
            }}
            qaIdentifier={QaIdentifier.Search_Filter_Section_Cancellation}
            filterList={
                isBoUser
                    ? [
                          {
                              isSelected:
                                  cancellation === CancellationType.Cost,
                              filterValue: t(
                                  'TRANS-free-or-cheap-cancellation'
                              ),
                              content:
                                  !specialMeta?.cancellation?.cost?.hasCount &&
                                  !cancellation ? null : (
                                      <Radio
                                          key={SearchParam.CancellationCost}
                                          input={{
                                              id: SearchParam.CancellationCost,
                                              name: SearchParam.CancellationCost,
                                          }}
                                          isChecked={
                                              cancellation ===
                                              CancellationType.Cost
                                          }
                                          isDisabled={
                                              cancellation !==
                                                  CancellationType.Cost &&
                                              specialMeta?.cancellation?.cost &&
                                              getDisabledStatus(
                                                  specialMeta?.cancellation
                                                      ?.cost
                                              )
                                          }
                                          isOnlyDeselectable={
                                              !specialMeta?.cancellation?.cost
                                                  ?.hasCount &&
                                              specialMeta?.cancellation?.cost
                                                  ?.count === 0 &&
                                              !cancellation
                                          }
                                          onChange={() => {
                                              onSelect(CancellationType.Cost);
                                              setCancellation((current) =>
                                                  current !==
                                                  CancellationType.Cost
                                                      ? CancellationType.Cost
                                                      : undefined
                                              );
                                          }}
                                          withUXSelection
                                          customClass={{
                                              Wrapper: classNames({
                                                  [styles.Changed]:
                                                      isFilterChanged(
                                                          CancellationType.Cost
                                                      ),
                                              }),
                                          }}
                                      >
                                          <FilterLabel
                                              labelText={t(
                                                  'TRANS-free-or-cheap-cancellation'
                                              )}
                                              tooltip={`<b>${t(
                                                  'TRANS-free-or-cheap-cancellation'
                                              )}</b><p>${t(
                                                  'TRANS-show-hotels-with-free-or-cheap-cancellation'
                                              )}</p>`}
                                              {...specialMeta?.cancellation
                                                  ?.cost}
                                          />
                                      </Radio>
                                  ),
                          },
                          {
                              isSelected:
                                  cancellation === CancellationType.Free24Hour,
                              filterValue: t(
                                  'TRANS-cancellable-free-of-charge-until-24h-before-departure'
                              ),
                              content: (
                                  <Radio
                                      key={
                                          FilterKeys.CancellationDaysBeforeTravelStart
                                      }
                                      input={{
                                          id: FilterKeys.CancellationDaysBeforeTravelStart,
                                          name: SearchParam.CancellationCost,
                                      }}
                                      isDisabled={
                                          cancellation !==
                                              CancellationType.Cost &&
                                          specialMeta?.cancellation?.cost &&
                                          getDisabledStatus(
                                              specialMeta?.cancellation?.cost
                                          )
                                      }
                                      isChecked={
                                          cancellation ===
                                          CancellationType.Free24Hour
                                      }
                                      onChange={() => {
                                          onSelect(CancellationType.Free24Hour);
                                          setCancellation((current) =>
                                              current !==
                                              CancellationType.Free24Hour
                                                  ? CancellationType.Free24Hour
                                                  : undefined
                                          );
                                      }}
                                      withUXSelection
                                      customClass={{
                                          Wrapper: classNames({
                                              [styles.Changed]: isFilterChanged(
                                                  CancellationType.Free24Hour
                                              ),
                                          }),
                                      }}
                                  >
                                      <FilterLabel
                                          labelText={t(
                                              'TRANS-cancellable-until-24h-before-departure'
                                          )}
                                          {...specialMeta?.cancellation
                                              ?.free24Hour}
                                      />
                                  </Radio>
                              ),
                          },
                      ]
                    : [
                          {
                              isSelected:
                                  cancellation === CancellationType.Cost,
                              filterValue: t(
                                  'TRANS-free-or-cheap-cancellation'
                              ),
                              content: (
                                  <Checkbox
                                      key={SearchParam.CancellationCost}
                                      input={{
                                          id: SearchParam.CancellationCost,
                                          name: SearchParam.CancellationCost,
                                      }}
                                      isChecked={
                                          cancellation === CancellationType.Cost
                                      }
                                      isOnlyDeselectable={
                                          specialMeta?.cancellation?.cost
                                              ?.hasCount &&
                                          specialMeta?.cancellation?.cost
                                              ?.count === 0 &&
                                          cancellation === CancellationType.Cost
                                      }
                                      onChange={() =>
                                          setCancellation((current) =>
                                              !current
                                                  ? CancellationType.Cost
                                                  : undefined
                                          )
                                      }
                                      isDisabled={
                                          cancellation !==
                                              CancellationType.Cost &&
                                          specialMeta?.cancellation?.cost &&
                                          getDisabledStatus(
                                              specialMeta?.cancellation?.cost
                                          )
                                      }
                                      withUXSelection
                                  >
                                      <FilterLabel
                                          labelText={t(
                                              'TRANS-free-or-cheap-cancellation'
                                          )}
                                          tooltip={`<b>${t(
                                              'TRANS-free-or-cheap-cancellation'
                                          )}</b><p>${t(
                                              'TRANS-show-hotels-with-free-or-cheap-cancellation'
                                          )}</p>`}
                                          {...specialMeta?.cancellation?.cost}
                                      />
                                  </Checkbox>
                              ),
                              //   ),
                          },
                      ]
            }
        />
    );
};

export default Cancellation;
